import type { ModeValue } from './dateUtils';
import { isValidDate } from './dateUtils';

type SelectDayOptions = {
  /** The committed value before this click. */
  selection: ModeValue;
  onSelect: (next: ModeValue) => void;
  onClose: () => void;
};

/**
 * Turns a click on a calendar day into the next value for the mode.
 *
 * In range mode a click completes the range when only its start is set, and
 * starts a new range otherwise. Time mode shows no days, and the time picker
 * commits through its own callback.
 */
export const selectDayHandler = (
  day: Date,
  { selection, onSelect, onClose }: SelectDayOptions,
) => {
  if (!isValidDate(day)) {
    return;
  }

  switch (selection.mode) {
    case 'single':
      onSelect({ mode: selection.mode, value: day });
      onClose();
      return;

    case 'single-time': {
      // A day click moves the date only; the time picker owns the time.
      const next = new Date(day);
      if (selection.value) {
        next.setHours(
          selection.value.getHours(),
          selection.value.getMinutes(),
          selection.value.getSeconds(),
          selection.value.getMilliseconds(),
        );
      }
      onSelect({ mode: selection.mode, value: next });
      return;
    }

    case 'range': {
      const { from, to } = selection.value ?? {};
      onSelect({
        mode: selection.mode,
        value:
          from && !to
            ? { from: day < from ? day : from, to: day < from ? from : day }
            : { from: day, to: undefined },
      });
      return;
    }

    case 'time':
      // Time mode shows no days; the time picker commits through onTimeChange.
      return;

    default: {
      const exhaustiveCheck: never = selection;
      return exhaustiveCheck;
    }
  }
};
